import {
  Component,
  Input,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  OnDestroy,
  Output,
  EventEmitter,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Gridster,
  GridsterConfig,
  GridsterItem,
  GridsterItemConfig,
  GridType,
  CompactType,
  DisplayGrid,
} from 'angular-gridster2';
import { DynamicComponentComponent } from '../dynamic-component/dynamic-component.component';
import { CustomComponent } from '../../models/CustomComponent.model';
import { DragService } from '../../services/drag/drag.service';
import { EditComponentModalComponent } from '../modals/edit-component-modal/edit-component-modal.component';
import { ComponentService } from '../../services/component/component.service';
import { ComponentInputBase } from '../../models/ComponentInputBase.model';
import { PortalModule, ComponentPortal } from '@angular/cdk/portal';
import { Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { AuthService } from '../../services/auth/auth.service';

@Component({
  selector: 'app-component-container',
  standalone: true,
  imports: [
    CommonModule,
    Gridster,
    GridsterItem,
    DynamicComponentComponent,
    PortalModule,
    OverlayModule,
  ],
  templateUrl: './component-container.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './component-container.component.scss',
})
export class ComponentContainerComponent
  extends ComponentInputBase
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('dropZone') dropZone!: ElementRef;
  @ViewChild('gridster') gridster!: Gridster;

  isModalOpen: boolean = false;
  editedComponent: CustomComponent | undefined;
  private _customComponent!: CustomComponent;

  @Input() set customComponent(value: CustomComponent) {
    this._customComponent = value;
    if (this._customComponent) {
      if (!this._customComponent.childComponents) {
        this._customComponent.childComponents = [];
      }

      // Sort child components by responsivePosition only if responsive
      if (
        this.isResponsive &&
        this._customComponent.childComponents.length > 0
      ) {
        this._customComponent.childComponents.sort((a, b) => {
          const posA = a.settings?.['responsivePosition']?.value ?? 0;
          const posB = b.settings?.['responsivePosition']?.value ?? 0;
          return posA - posB;
        });
      }

      // Recursively set editor mode for all nested components
      const setEditorMode = (component: CustomComponent) => {
        component.editorMode = this._customComponent.editorMode;
        component.childComponents?.forEach((child) => {
          setEditorMode(child);
        });
      };

      this._customComponent.childComponents.forEach((child) => {
        setEditorMode(child);
      });
    }
    this.cdr.markForCheck();
    this.refreshGridster();
  }

  get customComponent(): CustomComponent {
    return this._customComponent;
  }

  gridsterOptions: GridsterConfig = {};

  private resizeObserver: ResizeObserver | null = null;
  private lastResizeEvent?: number;

  @Output() childComponentsChange = new EventEmitter<CustomComponent[]>();

  get uuid(): string {
    return this._customComponent?.uuid || '';
  }

  get isResponsive(): boolean {
    return this.getInput('isResponsive') || false;
  }

  set isResponsive(value: boolean) {
    this.setInput('isResponsive', value);
  }

  private overlayRef: OverlayRef | null = null;

  constructor(
    private dragService: DragService,
    private cdr: ChangeDetectorRef,
    componentService: ComponentService,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private authService: AuthService
  ) {
    super(componentService);
    this.dynamicInput = {
      isResponsive: {
        type: 'boolean',
        value: false,
      },
    };
  }

  ngOnInit() {
    this.initializeGridsterOptions();
  }

  private initializeGridsterOptions() {
    this.gridsterOptions = {
      gridType: GridType.VerticalFixed,
      compactType: CompactType.None,
      margin: 0,
      outerMargin: false,
      enableEmptyCellDrop: false,
      enableOccupiedCellDrop: false,
      dragAndDrop: true,
      acceptWidgets: false,
      resizable: {
        enabled: true,
      },
      swap: false,
      pushItems: true,
      displayGrid: DisplayGrid.Always,
      minCols: 12,
      maxCols: 12,
      minRows: 30,
      maxRows: Infinity,
      rowHeight: 50,
      fixedRowHeight: 50,
      keepFixedHeightInMobile: true,
      keepFixedWidthInMobile: true,
      mobileBreakpoint: 0,
      responsiveView: false,
      draggable: {
        enabled: true,
        ignoreContent: true,
        dropOverItems: false,
        dragHandleClass: 'grid-item',
      },
      disablePushOnDrag: false,
      disablePushOnResize: false,
      pushDirections: { north: true, east: true, south: true, west: true },
      pushResizeItems: true,
      itemResizeCallback: () => this.refreshGridster(),
      itemChangeCallback: () => this.refreshGridster(),
      gridSizeChangedCallback: () => this.refreshGridster(),
    };
  }

  onDropToChild(event: any) {
    event.stopPropagation();
  }

  onComponentDropped(data: {
    event: DragEvent;
    item: GridsterItemConfig;
    x: number;
    y: number;
  }) {
    if (data.event) {
      data.event.stopPropagation();
    }
  }

  ngAfterViewInit() {

    if (!this.dropZone?.nativeElement) {
      console.warn('DropZone not initialized');
      return;
    }

    // Modified event listeners
    document.addEventListener('dragstart', () => {
      // Activate all drop zones when dragging starts
      document.querySelectorAll('.gridster-drop-zone').forEach((zone) => {
        zone.classList.add('dragging-active');
      });
    });

    document.addEventListener('dragend', () => {
      // Deactivate all drop zones when dragging ends
      document.querySelectorAll('.gridster-drop-zone').forEach((zone) => {
        zone.classList.remove('dragging-active');
        zone.classList.remove('drag-over');
        zone.classList.remove('drop-not-allowed');
      });
    });

    // Existing event listeners
    const dropZoneElement = this.dropZone.nativeElement;
    dropZoneElement.addEventListener(
      'dragenter',
      ((e: Event) => {
        this.onDragEnter(e as DragEvent);
      }) as EventListener,
      true
    );

    dropZoneElement.addEventListener(
      'dragleave',
      ((e: Event) => {
        this.onDragLeave(e as DragEvent);
      }) as EventListener,
      true
    );

    dropZoneElement.addEventListener(
      'dragover',
      ((e: Event) => {
        this.onDragOver(e as DragEvent);
      }) as EventListener,
      true
    );

    dropZoneElement.addEventListener(
      'drop',
      ((e: Event) => {
        this.onDrop(e as DragEvent);
      }) as EventListener,
      true
    );

    // DISABLED: Resize events causing infinite DOM recursion loops
    // setTimeout(() => {
    //   // Only dispatch resize if we haven't done so recently
    //   if (!this.lastResizeEvent || Date.now() - this.lastResizeEvent > 1000) {
    //     window.dispatchEvent(new Event('resize'));
    //     this.lastResizeEvent = Date.now();
    //   }
    // }, 100);

    this.setupResizeObserver();
  }

  private setupResizeObserver() {
    if (!this.gridster?.el?.parentElement) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.refreshGridster();
      this.cdr.detectChanges();
    });

    this.resizeObserver.observe(this.gridster.el.parentElement);
  }

  // Helper method to check if drop is allowed
  isDropAllowed(event: DragEvent): boolean {
    try {
      const dragData = this.dragService.getDragData();
      if (!dragData) return false;

      // Get the actual container UUID from the drop target
      const dropTarget = event.currentTarget as HTMLElement;
      const targetContainerUUID = dropTarget.getAttribute(
        'data-container-uuid'
      );

      // Prevent dropping into itself or its current container
      if (
        dragData.uuid === this.customComponent.uuid ||
        dragData.sourceUUID === this.customComponent.uuid ||
        targetContainerUUID === dragData.sourceUUID
      ) {
        return false;
      }

      return (
        dropTarget.classList.contains('gridster-drop-zone') ||
        dropTarget.classList.contains('nested')
      );
    } catch (error) {
      console.error('Drop check error:', error);
      return false;
    }
  }

  onDragEnter(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const dropZone = event.currentTarget as HTMLElement;
    dropZone?.classList.add('drag-over');

    // Add drop-not-allowed class if drop is not allowed
    if (!this.isDropAllowed(event)) {
      dropZone?.classList.add('drop-not-allowed');
    }
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const dropZone = event.currentTarget as HTMLElement;
    dropZone?.classList.remove('drag-over');
    dropZone?.classList.remove('drop-not-allowed');
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    const dropZone = event.currentTarget as HTMLElement;
    if (!this.isDropAllowed(event)) {
      dropZone?.classList.add('drop-not-allowed');
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'none';
      }
      return;
    }

    // Ensure the drop zone is active
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    dropZone?.classList.add('drag-over');
    dropZone?.classList.remove('drop-not-allowed');
  }

  onDrop(event: DragEvent) {
    try {
      event.preventDefault();
      event.stopPropagation();

      const dragData = this.dragService.getDragData();
      if (!dragData) {
        return;
      }

      // Get the actual container UUID from the drop target
      const dropZone = event.currentTarget as HTMLElement;
      const actualContainerUUID = dropZone.getAttribute('data-container-uuid');

      // Prevent dropping into itself or current parent
      if (
        dragData.uuid === actualContainerUUID ||
        dragData.parentUUID === actualContainerUUID
      ) {
        return;
      }

      const componentDroppedEvent = new CustomEvent('componentDropped', {
        bubbles: true,
        detail: {
          sourceUUID: dragData.uuid,
          containerUUID: actualContainerUUID,
          x: 0,
          y: this.customComponent.childComponents?.length || 0,
          mode: 1,
        },
      });

      dropZone.dispatchEvent(componentDroppedEvent);
      this.refreshGridster();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Drop error:', error);
    }
  }

  refreshGridster() {
    if (!this.gridster) return;

    // First update
    this.gridster.updateGrid();

    // Schedule multiple updates to ensure proper rendering
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      this.gridster.updateGrid();

      // One more update after a brief delay
      setTimeout(() => {
        this.gridster.updateGrid();
        this.cdr.detectChanges();
      }, 50);
    }, 0);
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  onSaveComponent(updatedComponent: CustomComponent) {
    if (this.editedComponent && this.customComponent.childComponents) {
      const index = this.customComponent.childComponents.findIndex(
        (comp) => comp.uuid === this.editedComponent!.uuid
      );

      if (index !== -1) {
        // Create new CustomComponent instance
        const newComponent = new CustomComponent(
          updatedComponent.name,
          updatedComponent.x,
          updatedComponent.y,
          updatedComponent.cols,
          updatedComponent.rows,
          updatedComponent.customCSS,
          updatedComponent.settings,
          updatedComponent.inputs,
          updatedComponent.mode,
          updatedComponent.uuid,
          updatedComponent.childComponents
        );

        // Copy over properties
        newComponent.component = updatedComponent.component;
        if (updatedComponent.componentInstance) {
          newComponent.componentInstance = updatedComponent.componentInstance;
        }

        // Create a new array reference while maintaining order
        const newChildComponents = [...this.customComponent.childComponents];
        newChildComponents[index] = newComponent;
        this.customComponent.childComponents = newChildComponents;

        // Force change detection and gridster updates
        this.cdr.markForCheck();
        this.refreshGridster();
        this.cdr.detectChanges();

        // Emit changes to parent
        this.childComponentsChange.emit(this.customComponent.childComponents);
      }
    }
    this.closeModal();
  }

  closeModal() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.isModalOpen = false;
    this.editedComponent = undefined;
  }

  editComponentFromGridster(uuid: string, templateUUID: null) {
    this.editedComponent = this.customComponent.childComponents?.find(
      (comp) => comp.uuid === uuid
    );
    this.isModalOpen = true;

    if (!this.overlayRef) {
      const positionStrategy = this.overlay
        .position()
        .global()
        .centerHorizontally()
        .centerVertically();

      this.overlayRef = this.overlay.create({
        positionStrategy,
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-dark-backdrop',
        panelClass: 'edit-modal-overlay',
      });

      const portal = new ComponentPortal(EditComponentModalComponent);
      const componentRef = this.overlayRef.attach(portal);

      // Set the inputs and outputs
      componentRef.instance.isOpen = true;
      componentRef.instance.component = this.editedComponent;
      componentRef.instance.saveChanges.subscribe((comp) =>
        this.onSaveComponent(comp)
      );
      componentRef.instance.closeModal.subscribe(() => this.closeModal());

      this.overlayRef.backdropClick().subscribe(() => {
        this.closeModal();
      });
    }
  }

  removeComponentFromGridster(component: CustomComponent, templateUUID: null) {
    if (confirm('Are you sure you want to remove this component?')) {
      const index = this.customComponent.childComponents?.indexOf(component);
      if (index !== undefined && index !== -1) {
        this.customComponent.childComponents?.splice(index, 1);
        this.refreshGridster();
      }
    }
  }

  copyComponentFromGridster(component: CustomComponent) {
    if (!this.customComponent.childComponents) {
      this.customComponent.childComponents = [];
    }

    const parentUUID = this.customComponent.uuid;
    if (!parentUUID) {
      console.error('Cannot copy component: container UUID is missing');
      return;
    }

    const clonedComponent = this.cloneChildComponentTree(component, parentUUID);
    clonedComponent.x = component.x ?? 0;
    clonedComponent.y = (component.y ?? 0) + (component.rows ?? 1);

    const sourceIndex = this.customComponent.childComponents.findIndex(
      (comp) => comp.uuid === component.uuid
    );
    if (sourceIndex === -1) {
      this.customComponent.childComponents.push(clonedComponent);
    } else {
      this.customComponent.childComponents.splice(
        sourceIndex + 1,
        0,
        clonedComponent
      );
    }

    if (this.isResponsive) {
      this.customComponent.childComponents.forEach((comp, idx) => {
        if (!comp.settings) comp.settings = {};
        comp.settings['responsivePosition'] = {
          type: 'number',
          value: idx,
        };
      });
    }

    this.childComponentsChange.emit(this.customComponent.childComponents);
    this.refreshGridster();
    this.cdr.detectChanges();
  }

  private cloneChildComponentTree(
    component: CustomComponent,
    parentUUID: string
  ): CustomComponent {
    const clonedSettings = component.settings
      ? JSON.parse(JSON.stringify(component.settings))
      : undefined;
    if (clonedSettings && 'customID' in clonedSettings) {
      clonedSettings['customID'] = { type: 'string', value: '' };
    }

    const clonedInputs = component.inputs
      ? JSON.parse(JSON.stringify(component.inputs))
      : undefined;
    const clone = new CustomComponent(
      component.name,
      component.x,
      component.y,
      component.cols,
      component.rows,
      component.customCSS,
      clonedSettings,
      clonedInputs,
      component.mode
    );

    clone.parentUUID = parentUUID;

    if (component.childComponents?.length) {
      clone.childComponents = component.childComponents.map((child) =>
        this.cloneChildComponentTree(child, clone.uuid!)
      );
    }

    return clone;
  }

  moveItem(index: number, direction: 'up' | 'down') {
    if (!this.customComponent.childComponents || !this.isResponsive) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= this.customComponent.childComponents.length)
      return;

    // Create a new array and swap items
    const updatedComponents = [...this.customComponent.childComponents];
    [updatedComponents[index], updatedComponents[newIndex]] = [
      updatedComponents[newIndex],
      updatedComponents[index],
    ];

    // Update positions in settings for each component
    updatedComponents.forEach((comp, idx) => {
      if (!comp.settings) comp.settings = {};
      comp.settings['responsivePosition'] = {
        type: 'number',
        value: idx,
      };
    });

    // Update reference
    this.customComponent.childComponents = updatedComponents;

    // Emit changes
    this.childComponentsChange.emit(this.customComponent.childComponents);
    this.cdr.detectChanges();
  }

  getCustomStyles(item: any): { [key: string]: string } {
    if (!item?.customCSS) {
      return {};
    }

    const cssText = item.customCSS;
    const styles: { [key: string]: string } = {};

    // Split the CSS text into individual rules and parse them
    const rules = cssText.split(';').filter((rule: string) => rule.trim());
    rules.forEach((rule: string) => {
      const [property, value] = rule
        .split(':')
        .map((part: string) => part.trim());
      if (property && value && property !== 'grid-row') {
        styles[property] = value;
      }
    });

    return styles;
  }

  onDragStart(event: DragEvent, item: CustomComponent) {

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      const dragData = {
        ...CustomComponent.convertToDTO(item),
        componentName: item.name,
        sourceUUID: item.uuid,
        parentUUID: item.parentUUID,
        isChild: true,
        isComponentContainer: item.name === 'ComponentContainerComponent',
        childComponents: item.childComponents?.map((child) =>
          CustomComponent.convertToDTO(child)
        ),
      };

      event.dataTransfer.setData('text/plain', JSON.stringify(dragData));
      this.dragService.setDragData(dragData);
    }
  }

  onDragEnd(event: DragEvent) {
    event.stopPropagation();
    this.dragService.clearDragData();
  }

  addChildComponent(component: CustomComponent) {
    if (!this.customComponent.childComponents) {
      this.customComponent.childComponents = [];
    }

    // Set the parent UUID when adding child components
    component.parentUUID = this.customComponent.uuid;
    this.customComponent.childComponents.push(component);

    // Emit changes
    this.childComponentsChange.emit(this.customComponent.childComponents);
  }

  onChildDragStart(event: DragEvent, item: CustomComponent) {

    // Prevent parent container drag
    event.stopPropagation();

    // Add dragging class to current element
    const element = event.currentTarget as HTMLElement;
    element.classList.add('dragging');

    if (this.customComponent?.uuid) {
      item.parentUUID = this.customComponent.uuid;
    }

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      const dragData = {
        ...CustomComponent.convertToDTO(item),
        componentName: item.name,
        sourceUUID: item.uuid,
        parentUUID: this.customComponent?.uuid,
        isChild: true,
        isComponentContainer: item.name === 'ComponentContainerComponent',
      };

      event.dataTransfer.setData('text/plain', JSON.stringify(dragData));
      this.dragService.setDragData(dragData);
    }
  }

  onChildDragEnd(event: DragEvent) {
    event.stopPropagation();

    // Remove dragging class from all elements
    document.querySelectorAll('.dragging').forEach((el) => {
      el.classList.remove('dragging');
    });

    this.dragService.clearDragData();
  }

  isComponentVisible(): boolean {
    const userRoles = this.authService.getUserRoles();
    const allowedRoles =
      this.customComponent?.settings?.['allowedRoles']?.value || [];

    // SuperAdmin can always see everything
    if (userRoles.includes('superadmin') || userRoles.includes('Superadmin')) {
      return true;
    }

    // If no allowed roles specified, component is visible to all
    if (!allowedRoles || allowedRoles.length === 0) {
      return true;
    }

    // Check if user has ANY of the roles that ARE allowed to see the component
    const hasAllowedRole = allowedRoles.some((role: string) =>
      userRoles.includes(role)
    );
    return hasAllowedRole;
  }

  isChildComponentVisible(childComponent: CustomComponent): boolean {
    const userRoles = this.authService.getUserRoles();
    const allowedRoles =
      childComponent?.settings?.['allowedRoles']?.value || [];

    // Debug logging
    // SuperAdmin can always see everything
    if (userRoles.includes('superadmin') || userRoles.includes('Superadmin')) {
      return true;
    }

    // If no allowed roles specified, component is visible to all
    if (!allowedRoles || allowedRoles.length === 0) {
      return true;
    }

    // Check if user has ANY of the roles that ARE allowed to see the component
    const hasAllowedRole = allowedRoles.some((role: string) =>
      userRoles.includes(role)
    );
    return hasAllowedRole;
  }
}
